"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useCallback } from "react";
import Link from "next/link";
import { ChevronDown, ChevronUp } from "lucide-react";

interface CategoryFiltersProps {
  brands: string[];
  stores: string[];
  attributes: { name: string; values: string[] }[];
  subcategories: { id: string; name: string; slug: string; count: number }[];
  deliveryMethods: { code: string; label: string; count: number }[];
  typeParam?: string;
}

function FilterSection({
  title,
  items,
  type,
  selectedValues = [],
  onToggle,
}: {
  title: string;
  items: { label: string; value: string; count?: number; href?: string }[];
  type: "checkbox" | "link";
  selectedValues?: string[];
  onToggle?: (value: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="border-b border-gray-200 py-4">
      <div className="font-semibold text-sm mb-3">{title}</div>
      <div className="space-y-2 max-h-[200px] overflow-y-auto">
        {items.map((item) =>
          type === "link" && item.href ? (
            <Link
              key={item.value}
              href={item.href}
              className="flex items-center justify-between text-sm text-gray-600 hover:text-secondary"
            >
              <span>{item.label}</span>
              {item.count !== undefined && (
                <span className="text-xs text-gray-400">({item.count})</span>
              )}
            </Link>
          ) : (
            <label
              key={item.value}
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer hover:text-secondary"
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(item.value)}
                onChange={() => onToggle?.(item.value)}
                className="rounded border-gray-300 text-secondary focus:ring-secondary"
              />
              <span className="flex-1">{item.label}</span>
              {item.count !== undefined && (
                <span className="text-xs text-gray-400">({item.count})</span>
              )}
            </label>
          )
        )}
      </div>
    </div>
  );
}

export function CategoryFilters({
  // brands volontairement masqué de l'UI (doublon avec stores) — gardé en prop pour compat
  brands: _brands,
  stores,
  attributes,
  subcategories,
  deliveryMethods,
  typeParam,
}: CategoryFiltersProps) {
  void _brands;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Lire les filtres actuels depuis l'URL
  const selectedBrands = searchParams.get("brands")?.split(",").filter(Boolean) || [];
  const selectedStores = searchParams.get("stores")?.split(",").filter(Boolean) || [];
  const selectedDelivery = searchParams.get("delivery")?.split(",").filter(Boolean) || [];

  // Lire les attributs depuis l'URL (format: attr_Poids=750kg,120kg)
  const selectedAttributes: Record<string, string[]> = {};
  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith("attr_")) {
      const attrName = key.replace("attr_", "");
      selectedAttributes[attrName] = value.split(",").filter(Boolean);
    }
  }

  // Mettre à jour l'URL avec les nouveaux filtres
  const updateFilters = useCallback(
    (key: string, values: string[]) => {
      const params = new URLSearchParams(searchParams.toString());

      if (values.length > 0) {
        params.set(key, values.join(","));
      } else {
        params.delete(key);
      }

      router.push(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [router, pathname, searchParams]
  );

  const toggleFilter = (key: string, currentValues: string[], value: string) => {
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];
    updateFilters(key, newValues);
  };

  const hasFilters =
    stores.length > 0 ||
    attributes.length > 0 ||
    subcategories.length > 0 ||
    deliveryMethods.length > 0;

  if (!hasFilters) return null;

  return (
    <div className="bg-white rounded-xl p-4">
      {/* Sous-catégories */}
      {subcategories.length > 0 && (
        <FilterSection
          title="Type de produit"
          type="link"
          items={subcategories.map((s) => ({
            label: s.name,
            value: s.id,
            count: s.count,
            href: `/categories/${s.slug}${typeParam ? `?type=${typeParam}` : ""}`,
          }))}
        />
      )}

      {/* Type de livraison */}
      {deliveryMethods.length > 0 && (
        <FilterSection
          title="Type de livraison"
          type="checkbox"
          items={deliveryMethods.map((d) => ({
            label: d.label,
            value: d.code,
            count: d.count,
          }))}
          selectedValues={selectedDelivery}
          onToggle={(value) => toggleFilter("delivery", selectedDelivery, value)}
        />
      )}

      {/* Vendeurs (les marques sont volontairement masquées — doublon avec vendeur) */}
      {stores.length > 0 && (
        <FilterSection
          title="Vendeurs"
          type="checkbox"
          items={stores.map((s) => ({ label: s, value: s }))}
          selectedValues={selectedStores}
          onToggle={(value) => toggleFilter("stores", selectedStores, value)}
        />
      )}

      {/* Attributs dynamiques */}
      {attributes.map((attr) => (
        <FilterSection
          key={attr.name}
          title={attr.name}
          type="checkbox"
          items={attr.values.map((v) => ({ label: v, value: v }))}
          selectedValues={selectedAttributes[attr.name] || []}
          onToggle={(value) =>
            toggleFilter(
              `attr_${attr.name}`,
              selectedAttributes[attr.name] || [],
              value
            )
          }
        />
      ))}

      {/* Bouton reset si des filtres sont actifs */}
      {(selectedBrands.length > 0 ||
        selectedStores.length > 0 ||
        selectedDelivery.length > 0 ||
        Object.keys(selectedAttributes).length > 0) && (
        <button
          onClick={() => router.push(pathname)}
          className="w-full mt-4 py-2 text-sm text-secondary hover:underline"
        >
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );
}
